import React, { useState, useEffect, ReactNode, useCallback } from 'react';
import { translateText } from '../../services/translationService';
import { useTranslationClient } from '../../hooks/useTranslationClient';
import { TranslationContext } from './TranslationContext';
import {
  normalizeLanguageCode,
  getTextNodesToTranslate,
  saveTranslationCache as saveCache,
  loadTranslationCache,
  getModalTextNodes as getModalTextNodesHelper
} from './translationHelpers';
import { useTranslationObserver } from './useTranslationObserver';
import { logger } from '../../utils/logger';

interface TranslationProviderProps {
  children: ReactNode;
}

// Create the translation provider component
export const TranslationProvider: React.FC<TranslationProviderProps> = ({ children }) => {
  // Initialize the Apollo client for translation service
  useTranslationClient();
  
  const [isTranslated, setIsTranslated] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [nodesBeingTranslated, setNodesBeingTranslated] = useState(0);
  const [pendingLanguageChange, setPendingLanguageChange] = useState<string | null>(null);
  const [translationLock, setTranslationLock] = useState<string | null>(null);
  
  // Helper function to ensure language consistency with localStorage
  const ensureLanguageConsistency = useCallback((currentLang: string): string => {
    try {
      const savedLanguage = localStorage.getItem('currentLanguage');
      if (savedLanguage && savedLanguage !== currentLang) {
        return savedLanguage;
      }
    } catch (error) {
      // Error checking language from localStorage
    }
    return currentLang;
  }, []);

  // Initialize currentLanguage from localStorage if available
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    try {
      const savedLanguage = localStorage.getItem('currentLanguage');
      return savedLanguage || 'EN';
    } catch (error) {
      // Error loading language from localStorage
      return 'EN';
    }
  });
  
  // Effect to ensure language is consistent with localStorage on component mount
  // This is CRITICAL for maintaining language state across page navigation
  useEffect(() => {
    try {
      const savedLanguage = localStorage.getItem('currentLanguage');
      if (savedLanguage) {
        if (savedLanguage !== currentLanguage) {
          setCurrentLanguage(savedLanguage);
          
          // If the saved language is not English, we need to ensure translation is enabled
          if (normalizeLanguageCode(savedLanguage) !== 'EN' && !isTranslated) {
            setIsTranslated(true);
          }
        }
      }
    } catch (error) {
      // Error checking language from localStorage
    }
  }, [currentLanguage, isTranslated]);
  
  // CRITICAL: Add a second effect that runs on every render to ensure language consistency
  // This helps catch cases where the component might partially re-render due to route changes
  useEffect(() => {
    const checkLanguageConsistency = () => {
      // Use the ensureLanguageConsistency helper function for consistency
      const consistentLanguage = ensureLanguageConsistency(currentLanguage);
      if (consistentLanguage !== currentLanguage) {
        setCurrentLanguage(consistentLanguage);
      }
    };
    
    // Check immediately
    checkLanguageConsistency();
    
    // Also set up a periodic check to ensure consistency
    const intervalId = setInterval(checkLanguageConsistency, 1000);
    
    return () => {
      clearInterval(intervalId);
    };
  }, [currentLanguage, ensureLanguageConsistency]);
  
  const [originalTexts, setOriginalTexts] = useState<Map<Element, string>>(new Map());
  const [isApplyingTranslations, setIsApplyingTranslations] = useState(false);
  const [processedNodes] = useState(new WeakSet<Node>());
  const [ignoreProcessedNodes, setIgnoreProcessedNodes] = useState(false);
  
  // Cache for translated texts to avoid retranslating the same text
  const [translationCache, setTranslationCache] = useState<Map<string, Map<string, string>>>(loadTranslationCache);
  
  // Function to save the translation cache to localStorage
  const saveTranslationCache = useCallback(() => {
    saveCache(translationCache);
  }, [translationCache]);
  
  // Function to clear the translation cache
  const clearTranslationCache = () => {
    // Clear the cache from state
    setTranslationCache(new Map());
    
    // CRITICAL: Clear ALL translation-related data from localStorage
    try {
      // Clear the translation cache
      localStorage.removeItem('translationCache');
      
      // Clear any other translation-related items
      localStorage.removeItem('currentLanguage');
      
      // Set the current language back to English
      setCurrentLanguage('EN');
      
    } catch (error) {
      logger.error('Error clearing translation data from localStorage:', error);
    }
    
    // If translation is currently active, revert it
    if (isTranslated) {
      // We'll define revertTranslation later, so we can't call it here
      // Instead, we'll set a flag to revert translation when it's defined
      setIsTranslated(false);
    }
    
    // WeakSet doesn't have a clear method, so we just set the flag to ignore processed nodes
    setIgnoreProcessedNodes(true);
  };

  // Helper function to apply a translation to an element and mark it appropriately
  const applyTranslation = useCallback((
    element: Element, 
    originalText: string, 
    translatedText: string, 
    targetLanguage: string
  ): void => {
    // Only mark as translated if the translation is different from the original text
    if (translatedText !== originalText) {
      // Apply the translation to the element's text content
      element.textContent = translatedText;
      
      // Mark the element as translated
      element.setAttribute('data-translated', 'true');
      element.setAttribute('data-translated-lang', targetLanguage);
    } else {      
      // Mark the element as attempted but not translated
      element.setAttribute('data-translated', 'false');
      element.setAttribute('data-translated-lang', targetLanguage);
      
      // Keep the original text
      element.textContent = originalText;
    }
  }, []);

  // Helper function to check if a node should be translated to the target language
  const shouldTranslateNode = useCallback((node: Node, targetLanguage: string): boolean => {
    // Skip non-text nodes or empty text nodes
    if (node.nodeType !== Node.TEXT_NODE || !node.textContent?.trim()) {
      return false;
    }
    
    // Skip nodes without a parent element
    const parent = node.parentElement;
    if (!parent) {
      return false;
    }
    
    // Get the node's current language and translation status
    const nodeLanguage = parent.getAttribute('data-translated-lang');
    const isTranslated = parent.getAttribute('data-translated');
    const hasOriginalText = parent.hasAttribute('data-original-text');
    const originalText = parent.getAttribute('data-original-text');
    
    // Log node details for debugging
    logger.info(`Node details: text="${node.textContent?.substring(0, 30)}...", language=${nodeLanguage}, isTranslated=${isTranslated}, hasOriginalText=${hasOriginalText}, originalText="${originalText?.substring(0, 30)}..."`);
    
    // CRITICAL: Skip nodes that are already translated to the target language
    if (nodeLanguage === targetLanguage) {
      logger.info(`Node is already in ${targetLanguage}, skipping`);
      return false;
    }
    
    // CRITICAL: Skip nodes that were previously attempted but failed to translate
    // This prevents repeatedly trying to translate text that can't be translated
    if (nodeLanguage === targetLanguage && isTranslated === 'false') {
      logger.info(`Node was previously attempted but not translated to ${targetLanguage}, skipping`);
      return false;
    }
    
    // Skip nodes in scripts, styles, and other non-visible elements
    const tagName = parent.tagName.toLowerCase();
    if (['script', 'style', 'noscript', 'option', 'code', 'pre'].includes(tagName)) {
      logger.info(`Node is in a ${tagName} element, skipping`);
      return false;
    }
    
    // Skip nodes that are part of the translation UI
    if (parent.closest('.translation-badge') || parent.closest('.translation-controls')) {
      return false;
    }
    
    // Skip already processed nodes (unless we're ignoring processed nodes)
    if (!ignoreProcessedNodes && processedNodes.has(node)) {
      return false;
    }
    
    return true;
  }, [ignoreProcessedNodes, processedNodes]);

    // Use the translation observer hook
  const { startObserving, stopObserving } = useTranslationObserver({
    isTranslated,
    isTranslating,
    isApplyingTranslations,
    currentLanguage,
    ignoreProcessedNodes,
    processedNodes,
    shouldTranslateNode,
    translateAddedNodes: (nodes, forcedLanguage) => translateAddedNodes(nodes, forcedLanguage, coreTranslateNodes),
    translateModalContent: () => translateModalContent(coreTranslateNodes),
    translationLock,
    setNodesBeingTranslated
  });

  // Helper function to get modal-specific text nodes
  const getModalTextNodes = useCallback((): Node[] => {
    return getModalTextNodesHelper(currentLanguage);
  }, [currentLanguage]);

  // Core translation function - simplified to remove throttling
  const coreTranslateNodes = useCallback(async (
    nodes: Node[], 
    targetLanguage: string, 
    isFullPageTranslation: boolean = false,
    stopObservingFn?: () => void,
    startObservingFn?: (lang?: string) => void
  ): Promise<boolean> => {
    
      // Skip translation if the language is English
      if (targetLanguage === 'EN') {
        return false;
      }
      
      // Only check if we're already translating
      if (isTranslating && !isFullPageTranslation) {
        return false;
      }

    // Set translating state
    setIsTranslating(true);
    
    // Increment the nodes being translated counter
    setNodesBeingTranslated(prev => prev + nodes.length);
    
    // Set flag to ignore mutations during translation
    setIsApplyingTranslations(true);
    
    // Stop observing DOM changes during translation to prevent cycles
    if (!isFullPageTranslation && stopObservingFn) {
      stopObservingFn();
    }
        
    try {
      if (nodes.length === 0) {
        logger.info('No text nodes to translate');
        return false;
      }
      
      // Always store original texts for all nodes we're about to translate
      // This ensures we can revert translations later
      const newOriginalTexts = new Map<Element, string>();
            
      nodes.forEach(node => {
        if (node.nodeType === Node.TEXT_NODE && node.parentElement && node.textContent) {
          
          // Only store original text if we haven't already
          if (!node.parentElement.hasAttribute('data-original-text')) {
            
            // If this parent already has text stored, append the new text
            if (newOriginalTexts.has(node.parentElement)) {
              const existingText = newOriginalTexts.get(node.parentElement) || '';
              newOriginalTexts.set(node.parentElement, existingText + node.textContent);
            } else {
              newOriginalTexts.set(node.parentElement, node.textContent);
            }
            
            // Store the original text in a data attribute for easier recovery
            try {
              node.parentElement.setAttribute('data-original-text', node.textContent);
              
              // Also mark it as English (original language)
              node.parentElement.setAttribute('data-original-lang', 'EN');
            } catch (e) {
              logger.error('Could not set data attribute:', e);
            }
          }
          
          // Mark this node as processed
          processedNodes.add(node);
        }
      });
      
      // Update the global originalTexts map if this is a full page translation
      if (isFullPageTranslation && !isTranslated) {
        setOriginalTexts(prev => {
          const merged = new Map(prev);
          newOriginalTexts.forEach((text, element) => {
            merged.set(element, text);
          });
          return merged;
        });
      }
      
      // Group text nodes by parent element to reduce API calls
      const parentToTexts = new Map<Element, string[]>();
      const parentToNodes = new Map<Element, Node[]>();
      const textsToTranslate: string[] = [];
      const nodeIndices: [Element, number][] = [];
      
      // Initialize cache for the language if it doesn't exist
      if (!translationCache.has(targetLanguage)) {
        translationCache.set(targetLanguage, new Map<string, string>());
        setTranslationCache(new Map(translationCache));
      }
      
      const currentLangCache = translationCache.get(targetLanguage)!;

      // Process nodes for translation
      nodes.forEach(node => {
        if (node.nodeType === Node.TEXT_NODE && node.parentElement && node.textContent?.trim()) {
          // CRITICAL: Only translate nodes that are in English or have no language attribute
          const parent = node.parentElement;
          const nodeLanguage = parent.getAttribute('data-translated-lang');
          
          // Skip nodes that are already translated to the target language
          if (nodeLanguage === targetLanguage) {
            return;
          }
          
          // Skip non-English nodes that are not in the target language
          if (nodeLanguage && nodeLanguage !== 'EN') {
            return;
          }
          
          if (!parentToTexts.has(parent)) {
            parentToTexts.set(parent, []);
            parentToNodes.set(parent, []);
          }
          
          const text = node.textContent;
          parentToTexts.get(parent)?.push(text);
          parentToNodes.get(parent)?.push(node);
          
          // Check if we need to translate this text
          if (!currentLangCache.has(text)) {
            textsToTranslate.push(text);
            nodeIndices.push([parent, parentToTexts.get(parent)!.length - 1]);
          }
          
          // Mark as processed if full page translation
          if (isFullPageTranslation) {
            processedNodes.add(node);
          }
        }
      });

      // Translate new texts
      if (textsToTranslate.length > 0) {

        try {
          const translatedTexts = await translateText(textsToTranslate, targetLanguage);
    
          // Update cache with new translations
          textsToTranslate.forEach((text, index) => {
            if (translatedTexts[index]) {
              currentLangCache.set(text, translatedTexts[index]);
            }
          });
          
          setTranslationCache(new Map(translationCache));
          saveTranslationCache();
          

          // Find all elements with data-original-text attribute
          document.querySelectorAll('[data-original-text]').forEach(element => {
            const originalText = element.getAttribute('data-original-text');
            if (!originalText) return;
            
            // Check if we have a translation for this text in the updated cache
            const translatedText = currentLangCache.get(originalText);
            if (translatedText) {
              
              // Use the helper function to apply the translation
              applyTranslation(element, originalText, translatedText, targetLanguage);
            }
          });
        } catch (error) {
          logger.error('Error translating texts:', error);
        }
      } else {

        // Find all elements with data-original-text attribute
        const elementsWithOriginalText = document.querySelectorAll('[data-original-text]');
        
        // Apply translations from cache to all elements with data-original-text
        elementsWithOriginalText.forEach(element => {
          const originalText = element.getAttribute('data-original-text');
          if (!originalText) return;
          
          const translatedText = currentLangCache.get(originalText);
          if (translatedText) {
            
            // Use the helper function to apply the translation
            applyTranslation(element, originalText, translatedText, targetLanguage);
          }
        });
      }
      
      // Skip DOM updates if the language has changed since we started translating
      // This is a normal part of operation when switching languages
      const currentNormalizedLanguage = normalizeLanguageCode(currentLanguage);
      if (currentNormalizedLanguage !== targetLanguage) {
        // Just return silently
        return false;
      }
      
      // No need to lock the language anymore
      
      // Apply translations from cache
      let appliedCount = 0;
      let missingCount = 0;
      
      // Function to mark a node as translated
      const markNodeAsTranslated = (node: Node, targetLang: string) => {
        if (node.parentElement) {
          try {
            // Only set data-translated to 'true' if the language is not English
            if (targetLang !== 'EN') {
              node.parentElement.setAttribute('data-translated', 'true');
            } else {
              // For English, remove the data-translated attribute or set it to 'false'
              node.parentElement.removeAttribute('data-translated');
            }
            
            // Set data-translated-lang to the target language
            node.parentElement.setAttribute('data-translated-lang', targetLang);
            
            // Ensure data-original-lang is set to 'EN' if not already set
            if (!node.parentElement.hasAttribute('data-original-lang')) {
              node.parentElement.setAttribute('data-original-lang', 'EN');
            }
            
            // CRITICAL: Ensure data-original-text is set if not already set
            if (!node.parentElement.hasAttribute('data-original-text') && node.textContent) {
              node.parentElement.setAttribute('data-original-text', node.textContent);
            }
          } catch (e) {
            logger.error('Could not set translation attributes:', e);
          }
        }
      };
      
      // Process all nodes for translation
      for (const [parent] of parentToTexts.entries()) {
        const nodes = parentToNodes.get(parent) || [];
        
        for (let i = 0; i < nodes.length; i++) {
          const node = nodes[i];
          if (!node.textContent) continue;
          
          // CRITICAL: Check again that the current language still matches the target language
          // This prevents race conditions where the language changes during the loop
          if (normalizeLanguageCode(currentLanguage) !== targetLanguage) {
            logger.info(`Language changed during node processing, stopping translation`);
            return false;
          }
          
          // CRITICAL: Use data-original-text if available, otherwise use node.textContent
          // This ensures we always translate from the original English text
          const storedOriginalText = parent.getAttribute('data-original-text');
          const textToTranslate = storedOriginalText || node.textContent;
          
          // Log for debugging
          if (storedOriginalText) {
            logger.info(`Using stored original text: "${storedOriginalText.substring(0, 30)}..." instead of current text: "${node.textContent.substring(0, 30)}..."`);
          }
          
          // CRITICAL: Always apply translations from cache, even if the node is already translated
          // This ensures that all nodes get translated, even if they're already in the cache
          const translatedText = currentLangCache.get(textToTranslate);
      
          if (translatedText) {
            // CRITICAL: Always apply the translation, even if it's the same as the original
            // This ensures that all nodes get translated, even if they're already in the target language
            
            // Mark the node as translated
            markNodeAsTranslated(node, targetLanguage);
            
            // Apply the translation
            node.textContent = translatedText;
            appliedCount++;
          } else {
            // Missing translation
            missingCount++;
          }
        }
      }      
      
      // Set translated state if this was a full page translation
      if (isFullPageTranslation) {
        setIsTranslated(true);
      }
      logger.debug('Translated count: ', appliedCount);
      logger.debug('No Translated count: ', missingCount);

      return true;
    } catch (error) {
      logger.error('Translation error:', error);
      return false;
    } finally {
      // Release the lock
      setTranslationLock(null);
      
      // Decrement the nodes being translated counter
      const newNodesBeingTranslated = Math.max(0, nodesBeingTranslated - nodes.length);
      setNodesBeingTranslated(newNodesBeingTranslated);
      
      // Only set isTranslating to false if there are no more nodes being translated
      // This ensures the loading indicator stays visible until all translations are complete
      if (newNodesBeingTranslated <= 0) {
        setIsTranslating(false);
      } else {
        // Keep isTranslating true to show the loading indicator
        setIsTranslating(true);
      }
      
      // Reset flag after translation is complete
      setIsApplyingTranslations(false);
      
      // Restart observing if we stopped it
    }
  }, [
    isTranslating, 
    setIsTranslating, 
    setIsApplyingTranslations, 
    processedNodes, 
    isTranslated, 
    setOriginalTexts, 
    translationCache, 
    setTranslationCache, 
    saveTranslationCache, 
    currentLanguage, 
    setIsTranslated, 
    setTranslationLock,
    nodesBeingTranslated,
    applyTranslation
  ]);

  // Function to translate added nodes
  const translateAddedNodes = useCallback(async (
    nodes: Node[], 
    forcedLanguage?: string,
    coreTranslateFn?: typeof coreTranslateNodes
  ) => {
    // CRITICAL: Always check the current language first
    // If the current language is English, we should never translate
    const consistentLanguage = ensureLanguageConsistency(currentLanguage);
    if (normalizeLanguageCode(consistentLanguage) === 'EN') {
      logger.info('Current language is English, no translation needed and stopping observer');
      // Stop the observer to prevent any further translations
      stopObserving();
      return;
    }
    
    // Use the provided language or fall back to the current language
    const requestedLanguage = normalizeLanguageCode(forcedLanguage || consistentLanguage);
    
    // Skip translation if the requested language is English
    if (requestedLanguage === 'EN') {
      return;
    }
    
    // CRITICAL: Only translate if isTranslated is true or if we're forcing translation
    // This prevents automatic translation when the user has disabled it
    if (!isTranslated && !forcedLanguage) {
      return;
    }
    
    // If the requested language doesn't match the current language, don't translate
    // This is a normal part of operation when switching languages
    const normalizedCurrentLang = normalizeLanguageCode(currentLanguage);
    if (requestedLanguage !== normalizedCurrentLang) {
      // Just return silently
      return;
    }
    
    // Filter nodes using our helper function
    const textNodes = nodes.filter(node => shouldTranslateNode(node, requestedLanguage));
    
    if (textNodes.length === 0) {
      return;
    }
    
    // Use the core translation function
    const translateFn = coreTranslateFn || coreTranslateNodes;
    await translateFn(textNodes, requestedLanguage, false);
  }, [ensureLanguageConsistency, currentLanguage, isTranslated, coreTranslateNodes, stopObserving, shouldTranslateNode]);

  // Function to specifically translate modal content
  const translateModalContent = useCallback(async (coreTranslateFn?: typeof coreTranslateNodes) => {
    if (!isTranslated) return;
    
    // CRITICAL: Get the current language BEFORE starting translation
    // This will be the single source of truth for this entire operation
    // Use ensureLanguageConsistency to make sure we're using the correct language from localStorage
    const consistentLanguage = ensureLanguageConsistency(currentLanguage);
    const targetLanguage = normalizeLanguageCode(consistentLanguage);
    logger.info(`Modal translation requested for language: ${targetLanguage}`);
    
    const modalNodes = getModalTextNodes();
    if (modalNodes.length > 0) {
      
      // Filter nodes using our shared helper function
      const textNodes = modalNodes.filter(node => shouldTranslateNode(node, targetLanguage));
      
      if (textNodes.length > 0) {
        
        // Check if the language has changed since we started preparing for modal translation
        if (normalizeLanguageCode(currentLanguage) !== targetLanguage) {
          // Just return silently
          return;
        }
        
        // Use the core translation function directly with the captured target language
        const translateFn = coreTranslateFn || coreTranslateNodes;
        await translateFn(textNodes, targetLanguage, false);
      } else {
        logger.info(`No nodes need translation in modals`);
      }
    }
  }, [isTranslated, ensureLanguageConsistency, currentLanguage, getModalTextNodes, shouldTranslateNode, coreTranslateNodes]);

  // Store interval ID in a ref so it can be accessed from stopTranslation
  const periodicScanIntervalRef = React.useRef<NodeJS.Timeout | null>(null);

  // Function to stop translation - can be used anywhere we need to stop the translation process
  const stopTranslation = useCallback(async (setToEnglish: boolean = true): Promise<void> => {
    logger.info(`Stopping translation, isTranslated: ${isTranslated}, originalTexts size: ${originalTexts.size}, setToEnglish: ${setToEnglish}`);
    
    // CRITICAL: Clear any running periodic scan interval
    if (periodicScanIntervalRef.current) {
      clearInterval(periodicScanIntervalRef.current);
      periodicScanIntervalRef.current = null;
    }
    
    // CRITICAL: Set isTranslated to false FIRST to stop the periodic scan
    // This prevents the periodic scan from running and incrementing the counter
    setIsTranslated(false);
    
    // CRITICAL: Reset the nodesBeingTranslated counter to 0
    // This ensures the spinner stops when translation is stopped
    setNodesBeingTranslated(0);
    
    // CRITICAL: Set isTranslating to false to stop the spinner
    setIsTranslating(false);
    
    // If we're setting to English, update the language state
    if (setToEnglish) {
      // CRITICAL: Set the current language to English FIRST before doing anything else
      // This ensures that any observers or watchers know we're switching to English
      setCurrentLanguage('EN');
      
      // Save the language to localStorage immediately
      try {
        localStorage.setItem('currentLanguage', 'EN');
      } catch (error) {
        logger.error('Error saving language to localStorage:', error);
      }
    }
    
    // Set flag to ignore mutations during reversion
    setIsApplyingTranslations(true);
    
    try {
      // Stop observing DOM changes - we don't want to observe anything during this process
      stopObserving();
      
      // CRITICAL: Find ALL elements with any translation-related attributes and reset them
      
      // First, restore original texts from the Map
      originalTexts.forEach((originalText, element) => {
        if (element.textContent) {
          element.textContent = originalText;
          
          // Remove all translation-related attributes
          try {
            element.removeAttribute('data-translated');
            element.removeAttribute('data-translated-lang');
            element.removeAttribute('data-original-lang');
            // Don't remove data-original-text as it might be needed for future translations
          } catch (e) {
            logger.error('Could not remove translation attributes:', e);
          }
        }
      });
      
      // Find ALL elements with any translation-related attributes and reset them in a single pass
      // This is more efficient than having separate loops for each attribute
      const translationAttributes = ['data-translated', 'data-translated-lang', 'data-original-lang'];
      const selector = translationAttributes.map(attr => `[${attr}]`).join(',');
      
      document.querySelectorAll(selector).forEach(element => {
        try {
          // Remove all translation-related attributes
          translationAttributes.forEach(attr => {
            if (element.hasAttribute(attr)) {
              element.removeAttribute(attr);
            }
          });
        } catch (e) {
          logger.error('Could not remove translation attributes:', e);
        }
      });
      
      // Also check for elements with data-original-text attribute that might not be in the originalTexts map
      document.querySelectorAll('[data-original-text]').forEach(element => {
        const originalText = element.getAttribute('data-original-text');
        if (originalText && element.textContent) {
          // Removed verbose logging
          element.textContent = originalText;
        }
      });
      
      // Toggle the flag to ignore processed nodes, allowing re-translation
      setIgnoreProcessedNodes(prev => !prev);
      
      // Always set isTranslated and isTranslating to false when stopping translation
      setIsTranslated(false);
      setIsTranslating(false);
      
      // Clear any pending language change again to be absolutely sure
      setPendingLanguageChange(null);
      
      logger.info('Translation stop complete - ALL translation attributes cleared');
    } finally {
      // Reset flag after reversion is complete
      setIsApplyingTranslations(false);
      
      // Ensure all translation states are reset
      setIsTranslated(false);
      setIsTranslating(false);
      setNodesBeingTranslated(0);
      setPendingLanguageChange(null);
    }
  }, [isTranslated, originalTexts, setIsApplyingTranslations, stopObserving, setIgnoreProcessedNodes, setIsTranslated, setCurrentLanguage, setPendingLanguageChange]);
  

  // Function to translate the page
  const translatePage = useCallback(async (lang?: string) => {
    // CRITICAL: Capture the current language state at the beginning of the operation
    // This will be the single source of truth for this entire translation operation
    // Use ensureLanguageConsistency to make sure we're using the correct language from localStorage
    const sourceLanguage = ensureLanguageConsistency(currentLanguage);
    
    // Use the provided language or fall back to the captured source language
    let languageToUse = lang || sourceLanguage;
    
    // Normalize the language code
    const normalizedLanguage = normalizeLanguageCode(languageToUse);
    
    // Skip if language is English
    if (normalizedLanguage === 'EN') {
      logger.info('Language is English, no translation needed');
      return;
    }
    
    // Check if the language has changed since we started setting up the translation
    // This is a normal part of operation when switching languages
    if (normalizeLanguageCode(currentLanguage) !== normalizeLanguageCode(sourceLanguage)) {
      // Just return silently
      return;
    }
    
    // Start observing DOM changes immediately to catch elements loading during translation
    // Pass the normalized language to ensure the observer uses the same language
    startObserving(normalizedLanguage);
    
    // Force a re-scan of the entire document
    setIgnoreProcessedNodes(true);
    
    // Get all text nodes to translate
    const textNodes = getTextNodesToTranslate(sourceLanguage);
    
    // Use the core translation function with the normalized language
    await coreTranslateNodes(textNodes, normalizedLanguage, true, stopObserving, startObserving);
  }, [ensureLanguageConsistency, currentLanguage, startObserving, coreTranslateNodes, stopObserving]);

  // The toggleTranslation function has been removed as its functionality
  // is now handled by the changeLanguage function

  // Function to change the language - simplified to remove throttling
  const changeLanguage = useCallback(async (lang: string): Promise<void> => {
    
    // Set isTranslating to true immediately to show the loading icon
    setIsTranslating(true);

    
    // Add a small delay to ensure the loading state is rendered before proceeding
    await new Promise(resolve => setTimeout(resolve, 50));
    
    // Normalize the language code
    const normalizedLang = normalizeLanguageCode(lang);
    logger.info(`Normalized language code from ${lang} to ${normalizedLang}`);
    
    try {
      // Set the pending language change
      setPendingLanguageChange(lang);
      
    // Stop observing DOM changes during language change to prevent cycles
    stopObserving();
    

      // Check if we're switching between two non-English languages
      const currentNormalizedLang = normalizeLanguageCode(currentLanguage);
      const isCurrentNonEnglish = currentNormalizedLang !== 'EN';
      const isTargetNonEnglish = normalizedLang !== 'EN';
      
      // If we're switching between non-English languages, stop the current translation first
      if (isCurrentNonEnglish && isTargetNonEnglish && currentNormalizedLang !== normalizedLang) {
 
        // CRITICAL: First, find ALL elements with data-translated-lang attribute and reset them
        // This ensures we don't have nodes with mixed languages
        document.querySelectorAll('[data-translated-lang]').forEach(element => {
          try {
            // Get the original text
            const originalText = element.getAttribute('data-original-text');
            if (originalText && element.textContent) {
              // Restore the original text
              element.textContent = originalText;
              
              // Remove the data-translated-lang attribute
              element.removeAttribute('data-translated-lang');
              
              // Keep data-original-text and data-original-lang for future translations
            }
          } catch (e) {
            logger.error('Could not reset element:', e);
          }
        });
        
        // This will clear all translation attributes but keep the current language
        await stopTranslation(false);
        
        // Wait a bit to ensure the DOM is cleared
        await new Promise(resolve => setTimeout(resolve, 200));
      }
      
      // Update the current language with the original language code
      // This preserves the original format for UI display purposes
      setCurrentLanguage(lang);
      
      // Save the language to localStorage
      try {
        localStorage.setItem('currentLanguage', lang);
      } catch (error) {
        logger.error('Error saving language to localStorage:', error);
      }
      
      // If changing to English, revert translation and we're done
      if (normalizedLang === 'EN') {
        if (isTranslated) {
          await stopTranslation();
        }
        
        setPendingLanguageChange(null);
        logger.info('Changed to English, translation is off');
        return;
      }
      
      setIsTranslated(true);
      
      // CRITICAL: First, update all existing translated nodes to the new language
      // This ensures we don't have nodes with mixed languages
      setIsApplyingTranslations(true);
      
      try {
        // Get all nodes with data-translated attribute
        const translatedNodes = document.querySelectorAll('[data-translated]');
        
        // Initialize cache for the language if it doesn't exist
        if (!translationCache.has(normalizedLang)) {
          translationCache.set(normalizedLang, new Map<string, string>());
          setTranslationCache(new Map(translationCache));
        }
        
        const currentLangCache = translationCache.get(normalizedLang)!;
        
        // Update each node to the new language
        for (const element of translatedNodes) {
          const originalText = element.getAttribute('data-original-text');
          if (!originalText) continue;
          
          // Check if we have a translation for this text
          if (currentLangCache.has(originalText)) {
            const translatedText = currentLangCache.get(originalText);
            if (translatedText) {
              element.textContent = translatedText;
              element.setAttribute('data-translated-lang', normalizedLang);
            }
          }
        }
      } finally {
        setIsApplyingTranslations(false);
      }
      
      // Then translate any remaining untranslated content
      // CRITICAL: Use the normalized language to ensure consistency
      // This is the most important part to prevent language cycling
      await translatePage(normalizedLang);
      
      // Clear the pending language change
      setPendingLanguageChange(null);
      
      // Start observing DOM changes after translation is complete
      // CRITICAL: Pass the normalized language to ensure the observer uses the same language
      startObserving(normalizedLang);
      

    } catch (error) {
      logger.error('Error changing language:', error);
      
      // Reset pending language change on error
      setPendingLanguageChange(null);
      
      // Restart observing in case of error
      // CRITICAL: Pass the normalized language to ensure the observer uses the same language
      startObserving(normalizedLang);
      
      // Rethrow the error for the caller to handle
      throw error;
    } finally {
      // Reset isTranslating state after translation is complete
      // This allows the periodic scan to run again
      setTimeout(() => {
        logger.info('Resetting isTranslating state after language change');
        setIsTranslating(false);
      }, 1000); // Add a small delay to ensure the UI updates
    }
  }, [
    setIsTranslating, 
    stopObserving, 
    setCurrentLanguage, 
    isTranslated, 
    stopTranslation, 
    setPendingLanguageChange, 
    setIsTranslated, 
    setIsApplyingTranslations, 
    translationCache, 
    setTranslationCache, 
    translatePage, 
    startObserving,
    currentLanguage
  ]);

  // Set up message listener for iframe communication
  useEffect(() => {
    // Function to handle messages from iframes
    const handleIframeMessage = async (event: MessageEvent) => {
      // Check if the message has the expected format
      if (!event.data || typeof event.data !== 'object' || !event.data.type) {
        return;
      }
      
      // Handle translation request message
      if (event.data.type === 'TRANSLATE_REQUEST') {
        
        const { texts, language: rawLanguage } = event.data;
        
        // Normalize the language code
        const language = normalizeLanguageCode(rawLanguage);
        
        
        // Check if the requested language matches the current language
        // Use ensureLanguageConsistency to make sure we're using the correct language from localStorage
        const consistentLanguage = ensureLanguageConsistency(currentLanguage);
        const currentNormalizedLanguage = normalizeLanguageCode(consistentLanguage);
        if (language !== currentNormalizedLanguage) {
          // Language mismatch is a normal part of operation when switching languages
          // Just send back the original texts and return silently
          const errorResponse = {
            type: 'TRANSLATION_RESPONSE',
            translations: texts, // Send back original texts as fallback
            language: language,
            originalLanguage: rawLanguage,
            error: true,
            errorMessage: 'Language mismatch'
          };
          
          (event.source as WindowProxy)?.postMessage(errorResponse, '*');
          return;
        }
        
        if (Array.isArray(texts) && texts.length > 0 && language) {
          try {
            
            // Initialize cache for this language if it doesn't exist
            if (!translationCache.has(language)) {
              translationCache.set(language, new Map<string, string>());
              setTranslationCache(new Map(translationCache));
            }
            
            const currentLangCache = translationCache.get(language)!;
            
            // Check which texts are already in the cache
            const textsToTranslate: string[] = [];
            const translatedTexts: string[] = new Array(texts.length);
            
            texts.forEach((text, index) => {
              if (currentLangCache.has(text)) {
                // Use cached translation
                const cachedTranslation = currentLangCache.get(text)!;
                
                // Check if the cached translation is actually different from the original
                if (cachedTranslation !== text) {
                  translatedTexts[index] = cachedTranslation;
                } else {
                  // The "translation" is identical to the original text
                  // This likely means the text is already in the target language
                  // Keep it in the cache to prevent repeated translation attempts
                  logger.info(`Text appears to be already in ${language} (or untranslatable) for iframe: "${text.substring(0, 30)}..."`);
                  
                  // Use the original text as the translation
                  translatedTexts[index] = text;
                }
              } else {
                // Need to translate this text
                textsToTranslate.push(text);
                // Store the index for later
                (translatedTexts as any)[`index_${textsToTranslate.length - 1}`] = index;
              }
            });
            
            // Only call the API if there are new texts to translate
            if (textsToTranslate.length > 0) {
              const newTranslations = await translateText(textsToTranslate, language);
              
              // Update cache with new translations
              textsToTranslate.forEach((text, i) => {
                if (newTranslations[i]) {
                  // Get the original index
                  const originalIndex = (translatedTexts as any)[`index_${i}`];
                  // Store in the result array
                  translatedTexts[originalIndex] = newTranslations[i];
                  // Update the cache
                  currentLangCache.set(text, newTranslations[i]);
                }
              });
              
              // Save the updated cache
              setTranslationCache(new Map(translationCache));
              saveTranslationCache();
            }
            
            // Send the translations back to the iframe
            const response = {
              type: 'TRANSLATION_RESPONSE',
              translations: translatedTexts,
              language: language,
              originalLanguage: rawLanguage // Include the original language code for reference
            };
            
            (event.source as WindowProxy)?.postMessage(response, '*');
            logger.info(`Sent ${translatedTexts.length} translations to iframe:`, event.origin);
          } catch (error) {
            logger.error('Error translating texts for iframe:', error);
            
            // Send error response
            const errorResponse = {
              type: 'TRANSLATION_RESPONSE',
              translations: texts, // Send back original texts as fallback
              language: language,
              originalLanguage: rawLanguage, // Include the original language code for reference
              error: true
            };
            
            (event.source as WindowProxy)?.postMessage(errorResponse, '*');
          }
        } else {
          logger.error('Invalid translation request:', event.data);
        }
      }
    };
    
    // Add message event listener
    window.addEventListener('message', handleIframeMessage);
    
    // Clean up
    return () => {
      window.removeEventListener('message', handleIframeMessage);
    };
  }, [translationCache, currentLanguage, saveTranslationCache, ensureLanguageConsistency]);

  // Effect to update iframes when translation state changes
  useEffect(() => {
    // Update all iframes with the current translation state
    const updateIframes = () => {
      const iframes = document.querySelectorAll('iframe');
      
      iframes.forEach(iframe => {
        // For all iframes with a contentWindow, try to use postMessage
        if (iframe.contentWindow) {
          try {
            // Use ensureLanguageConsistency to make sure we're using the correct language from localStorage
            const consistentLanguage = ensureLanguageConsistency(currentLanguage);
            const normalizedLanguage = normalizeLanguageCode(consistentLanguage);
            
            // Send the current translation state
            iframe.contentWindow.postMessage({
              type: 'TRANSLATE_CONTENT',
              language: normalizedLanguage,
              originalLanguage: currentLanguage, // Include the original language code for reference
              action: isTranslated ? 'translate' : 'revert'
            }, '*');
          } catch (error) {
            logger.error('Error posting message to iframe:', error);
          }
        }
      });
    };
    
    // Call immediately
    updateIframes();
    
    // Also set up a periodic check for new iframes
    const checkForNewIframes = setInterval(updateIframes, 2000);
    
    // Clean up
    return () => {
      clearInterval(checkForNewIframes);
    };
  }, [isTranslated, currentLanguage, ensureLanguageConsistency]);
  
  // Function to directly translate nodes without using coreTranslateNodes
  // This is used by the periodic scan to translate nodes even when isTranslating is true
  const directTranslateNodes = useCallback(async (
    nodes: Node[],
    targetLanguage: string
  ): Promise<boolean> => {
    logger.info(`directTranslateNodes called with ${nodes.length} nodes, language=${targetLanguage}`);
    
    // Set isTranslating to true at the start of the translation process
    setIsTranslating(true);
    logger.info('[LOADER] Set isTranslating to true in directTranslateNodes');
    
    // Skip if no nodes to translate
    if (nodes.length === 0) {
      logger.info('No text nodes to translate');
      // Force reset isTranslating to false when there are no nodes to translate
      setIsTranslating(false);
      return false;
    }
    
    // Skip if language is English
    if (targetLanguage === 'EN') {
      logger.info('Target language is English, no translation needed');
      // Force reset isTranslating to false when language is English
      setIsTranslating(false);
      return false;
    }
    
    try {
      // Initialize cache for the language if it doesn't exist
      if (!translationCache.has(targetLanguage)) {
        translationCache.set(targetLanguage, new Map<string, string>());
        setTranslationCache(new Map(translationCache));
      }
      
      const currentLangCache = translationCache.get(targetLanguage)!;
      
      // Collect texts that need translation
      const textsToTranslate: string[] = [];
      const nodesToUpdate: { node: Node, originalText: string }[] = [];
      
      // First pass: identify nodes that need translation
      for (const node of nodes) {
        if (node.nodeType !== Node.TEXT_NODE || !node.parentElement || !node.textContent?.trim()) {
          continue;
        }
        
        const parent = node.parentElement;
        
        // Skip nodes that are already translated to the target language
        if (parent.getAttribute('data-translated-lang') === targetLanguage) {
          continue;
        }
        
        // Get the original text
        let originalText = parent.getAttribute('data-original-text') || node.textContent;
        
        // Store original text if not already stored
        if (!parent.hasAttribute('data-original-text')) {
          parent.setAttribute('data-original-text', originalText);
          parent.setAttribute('data-original-lang', 'EN');
        }
        
        // Check if we have a translation for this text
        if (currentLangCache.has(originalText)) {
          const translatedText = currentLangCache.get(originalText);
          if (translatedText) {
            logger.info(`Applying cached translation: "${originalText.substring(0, 30)}..." -> "${translatedText.substring(0, 30)}..."`);
            
            // Apply the translation
            node.textContent = translatedText;
            
            // Mark the node as translated
            parent.setAttribute('data-translated', 'true');
            parent.setAttribute('data-translated-lang', targetLanguage);
          }
        } else {
          // Add to list of texts to translate
          textsToTranslate.push(originalText);
          nodesToUpdate.push({ node, originalText });
        }
      }
      
      // Second pass: translate texts that aren't in the cache
      if (textsToTranslate.length > 0) {
        logger.info(`Translating ${textsToTranslate.length} new texts in periodic scan`);
        
        try {
          // Call the translation service
          const translatedTexts = await translateText(textsToTranslate, targetLanguage);
          
          // Update the cache with new translations
          textsToTranslate.forEach((text, index) => {
            if (translatedTexts[index]) {
              currentLangCache.set(text, translatedTexts[index]);
            }
          });
          
          // Save the updated cache
          setTranslationCache(new Map(translationCache));
          saveTranslationCache();
          
          // Apply the translations to the nodes
          nodesToUpdate.forEach(({ node, originalText }, index) => {
            if (translatedTexts[index] && node.parentElement) {
              // Use the helper function to apply the translation
              applyTranslation(node.parentElement, originalText, translatedTexts[index], targetLanguage);
            }
          });
        } catch (error) {
          logger.error('Error translating texts in periodic scan:', error);
        }
      }
      
      return true;
    } catch (error) {
      logger.error('Error in directTranslateNodes:', error);
      return false;
    }
  }, [translationCache, saveTranslationCache, applyTranslation]);

      // Effect to periodically scan for untranslated content and fix incorrectly translated content
      useEffect(() => {
        // Clear any existing interval first
        if (periodicScanIntervalRef.current) {
          logger.info('Clearing existing periodic scan interval');
          clearInterval(periodicScanIntervalRef.current);
          periodicScanIntervalRef.current = null;
        }
        
        // Only run if translation is active AND language is not English
        if (!isTranslated || normalizeLanguageCode(currentLanguage) === 'EN') {
          logger.info('Periodic scan disabled: translation is inactive or language is English');
          return;
        }
        
        logger.info('Setting up periodic scan for untranslated content and incorrectly translated content');
    
  // Function to scan for untranslated content
      const scanForUntranslatedContent = () => {
        // Skip if we're applying translations or if language is English or if translation is disabled
        if (isApplyingTranslations || normalizeLanguageCode(currentLanguage) === 'EN' || !isTranslated) {
          return;
        }
        
        // CRITICAL: Always force a re-scan of the entire document by setting ignoreProcessedNodes to true
        // This ensures we don't miss any nodes that might have been added since the last scan
        setIgnoreProcessedNodes(true);
        
        // Use ensureLanguageConsistency to make sure we're using the correct language from localStorage
        const consistentLanguage = ensureLanguageConsistency(currentLanguage);
        const normalizedLang = normalizeLanguageCode(consistentLanguage);
        
        // Get all text nodes in the document
        const textNodes = getTextNodesToTranslate(currentLanguage);
        
        if (textNodes.length > 0) {
          // Use the direct translation function
          directTranslateNodes(textNodes, normalizedLang);
        } else {
          // Force reset isTranslating to false when there are no nodes to translate
          setIsTranslating(false);
          // Also reset nodesBeingTranslated to 0
          setNodesBeingTranslated(0);
        }
      };
    
    // Run immediately
    scanForUntranslatedContent();
    
    // Set up interval to run every 2 seconds
    periodicScanIntervalRef.current = setInterval(scanForUntranslatedContent, 2000);
    
    // Clean up on unmount or when translation is disabled
    return () => {
      if (periodicScanIntervalRef.current) {
        logger.info('Clearing periodic scan interval on cleanup');
        clearInterval(periodicScanIntervalRef.current);
        periodicScanIntervalRef.current = null;
      }
    };
  }, [isTranslated, isTranslating, isApplyingTranslations, currentLanguage, translateAddedNodes, setIgnoreProcessedNodes, ensureLanguageConsistency, directTranslateNodes]);

  return (
    <TranslationContext.Provider
      value={{
        isTranslated,
        isTranslating,
        nodesBeingTranslated,
        currentLanguage,
        pendingLanguageChange,
        translationLock,
        originalTexts,
        translationCache,
        changeLanguage,
        translatePage,
        stopTranslation,
        clearTranslationCache
      }}
    >
      {children}
    </TranslationContext.Provider>
  );
};

export default TranslationProvider;
